import React, { useCallback, useEffect, useMemo, useState } from "react";
import {Button, Card, Col, Modal, ModalFooter, ModalHeader, Row, Spinner} from "reactstrap";
import {VsiBubble, VsiH4} from "../../../styled/App.styled";
import AtAGlance from "../../../components/AtAGlance";
import {useVsiAppContext} from "../../../stores/VsiApp";
import Grid from "../../../components/Grid";
import {virtualGridDefs} from "../../../utils/gridColumnDefs/virtual/virtualUnits";
import {useDataWithRoute, useTitleFromComponent} from "../../../utils/hooks";
import {useNavigate} from "react-router-dom";
import DonutChart from "../../../components/DonutChart";
import {formatStockChartConfig} from "../../../utils/Helpers";
import Stockchart from "../../../components/Stockchart";
import PieGrid from "../../../components/PieGrid";
import {PostAuth} from "../../../utils/Verbs";

const virtualSummaryAag = [
    {
        field: "clusters",
        mobile: true
    },
    {
        field: "esx_hosts",
        mobile: true
    },
    {
        field: "processors",
        mobile: true
    },
    {
        field: "data_stores",
        diff: true,
        mobile: true
    },
    {
        field: "data_store_allocation",
        mobile: true
    },
    {
        field: "data_store_used",
        mobile: true
    },
    {
        field: "vm_build_capacity",
        mobile: true
    },
    {
        field: "total_free",
        diff: true,
        mobile: true

    },
    {
        field: "total_vms",
        diff: true,
        mobile: true

    },
    {
        field: "active_vms",
        diff: true,
        mobile: true

    },
    {
        field: "data_centers",
        diff: true,
        mobile: true

    }
];

const chartConfig = {
    yAxis: [
      {
          title: {
              text: "# of VMs"
          }
      }
      ],
    colors: ["#004577", "#ff8d02"]
};

export default function VirtualSummary({context, title}) {
    useTitleFromComponent(`${context} / ${title}`);
    const {theme, client, user, periodId, getAccessTokenSilently} = useVsiAppContext();
    const { clientId } = client;
    const [atAGlanceData, setAtAGlanceData] = useState({});
    const [pieChart, setPieChart] = useState({
        name: "Datacenters",
        colorByPoint: true,
        data: []
    });
    const [donutChart, setDonutChart] = useState([]);

    const [rowSelected, setRowSelected] = useState({});
    const [showWarningModal, setShowWarningModal] = useState(false);
    let navigate = useNavigate();

    const [data, { isLoading, reFetchData }] = useDataWithRoute(`/api/v6/report/enterprise_virtual_summary_proc?client_id=${clientId}&period_id=${periodId}`);
    const [
      gridData,
      trendData,
      // // aAGData // leaving this here as it is something that is returned
    ] = data?.recordsets ?? [[], [], []];

    const getDashboard = useCallback(passedData => {
        let totalData = {
            high_score: 0,
            medium_score: 0,
            low_score: 0,
            cpu_constrain: 0,
            memory_constrain: 0,
            disk_constrain: 0,
            clusters: 0,
            esx_hosts: 0,
            processors: 0,
            // ==== 4263 ====
            data_stores: 0,
            data_store_allocation: 0,
            data_store_used: 0,
            total_free: 0,
            // ==============
            vm_build_capacity: 0,
            total_vms: 0,
            active_vms: 0,
            data_centers: 0
        };

        const allDcs = {};
        (passedData ?? []).forEach(d => {
            // at a glance ribbon
            totalData.clusters += d.num_clusters || 0;
            totalData.esx_hosts += d.num_esx_hosts || 0;
            totalData.processors += d.total_processors || 0;
            totalData.vm_build_capacity += d.vm_build_capacity || 0;
            totalData.total_vms += d.num_vms || 0;
            totalData.active_vms += d.num_active_vms || 0;
            totalData.data_centers += d.num_data_centers || 0;
            // 4264
            totalData.data_stores += d.num_data_stores || 0;
            totalData.data_store_allocation += d.datastore_allocation || 0;
            totalData.data_store_used += d.datastore_used || 0;
            totalData.total_free += d.free_space || 0;


            // donut
            totalData.high_score += d.high_score;
            totalData.medium_score += d.medium_score;
            totalData.low_score += d.low_score;
            totalData.cpu_constrain += d.cpu_constrain;
            totalData.memory_constrain += d.memory_constrain;
            totalData.disk_constrain += d.disk_constrain;

            const dcName = d.datacenter_name || "Unspecified";

            if (!allDcs.hasOwnProperty(dcName)) {
                allDcs[dcName] = {
                    buildCapacity: 0
                };
            }
            allDcs[dcName].buildCapacity = allDcs[dcName].buildCapacity + d.vm_build_capacity;
        });

        const donutSeries = [
            {name: "Over Utilized (> 64%)", y: totalData["high_score"]},
            {name: "In Range (16-64%)", y: totalData["medium_score"]},
            {name: "Under Utilized (< 15%)", y: totalData["low_score"]}
        ];

        const pieSeries = Object.keys(allDcs).map(dc => {
            return {
                name: dc,
                y: allDcs[dc].buildCapacity
            };
        });

        setPieChart(pieChart => ({
            ...pieChart,
            data: pieSeries
        }));

        setDonutChart(donutSeries);
        setAtAGlanceData(totalData);

    }, [setPieChart, setDonutChart, setAtAGlanceData]);

    const setVirtualSummary = useCallback(() => {
        getDashboard(gridData);
    }, [gridData]);

    const filterChanged = useCallback(eventData => {
        let rowData = [];
        eventData.api.forEachNodeAfterFilter(node => {
            rowData.push(node.data);
        });
        getDashboard(rowData || []);
    }, [getDashboard]);

    const trendChart = useMemo(() => {
         return formatStockChartConfig(
            trendData || [], [
                {
                    name: "VMs with Memory Ballooning",
                    field: "ballooned_memory_count"
                },
                {
                    name: "VMs with CPU Ready",
                    field: "cpu_ready_count"
                }
            ]).series;
    }, [trendData]);

    const onRowClick = useCallback(e => {
        navigate(`/reports/virtual/vcenter/data-center-summary?client_id=${clientId}&period_id=${periodId}&vcenter_name=${encodeURI(e.data.vcenter_name)}&data_center_name=${encodeURI(e.data.datacenter_name)}`);
    }, [clientId, periodId, navigate]);

    const onSelectionChanged = useCallback(gridRef => {
        const selectedRow = gridRef?.current?.api?.getSelectedRows?.()?.[0];
        if (selectedRow) {
            setRowSelected(selectedRow);
        }
    }, [setRowSelected]);

    useEffect(() => {
        setVirtualSummary();
    }, [setVirtualSummary]);

    const deleteDeviceBtn = <>
        <Button
            onClick={() => {
                setShowWarningModal(true);
            }}
            id={"upload-trash-btn"}
            disabled={!rowSelected}>
            <i className="fa fa-trash" aria-hidden="true"></i>
        </Button>
        <Modal className="warning-modal" backdrop="static" isOpen={showWarningModal}>
            <ModalHeader toggle={() => setShowWarningModal(false)}>Are you sure you want to delete
                vCenter => {rowSelected?.device_name}</ModalHeader>
            <ModalFooter>
                <Button onClick={async () => {
                    const formData = new FormData();
                    formData.append("client_id", rowSelected.client_id);
                    formData.append("period_id", rowSelected.period_id);
                    formData.append("device_name", rowSelected.device_name);
                    formData.append("device_type_code", rowSelected.device_type_code);
                    const token = await getAccessTokenSilently();
                    PostAuth('/api/v6/upload/delete_device', formData, token)
                        .then(data => {
                            PostAuth('/api/v6/report/delete_virtual_summary',
                                {
                                    client_id: rowSelected.client_id,
                                    period_id: rowSelected.period_id,
                                    device_name: rowSelected.device_name
                                }, token).then(() => {
                                reFetchData();
                                setShowWarningModal(false);
                            });
                        });
                }
                } color="danger">Confirm Delete</Button>
                <Button color="secondary" onClick={() => {
                    setShowWarningModal(false)
                }}>Cancel</Button>
            </ModalFooter>
        </Modal>
    </>

    return (
        <Card className={`report-card p-2 nav-card-${theme}`}>
            <VsiH4>
                Virtual Summary
                {isLoading && <Spinner className={"ms-2"} size={"sm"} color={"secondary"}/>}
            </VsiH4>
            <VsiBubble className={"d-flex flex-column justify-content-center"}>
                <AtAGlance config={virtualSummaryAag}
                           data={atAGlanceData}
                           size={"lg"}
                           isLoading={isLoading}/>
            </VsiBubble>
            <VsiBubble className={"d-flex flex-column justify-content-center my-2"} noAutobot>
                <Row className={`py-2`}>
                    <Col md={12} lg={4}>
                        <VsiBubble>
                            <DonutChart data={donutChart}
                                        height={473}
                                        alignTitle={"center"}
                                        title={"Right Sizing Summary"}
                                        colors={["#004577", "#8ed600", "#ff8d02"]}
                                        seriesName={"VMs Capacity Scores"}
                                        enableLegend={true}
                                    />
                        </VsiBubble>
                    </Col>
                    <Col md={12} lg={4}>
                        <VsiBubble autobotName="PieDonutChart">
                            <PieGrid data={pieChart.data}
                                     alignTitle={"center"}
                                     title={"VM Build Capacity by Data Center"}
                                     gridConfig={['Data Center', 'VM Build']}/>
                        </VsiBubble>
                    </Col>
                    <Col md={12} lg={4}>
                        <VsiBubble>
                            <Stockchart series={trendChart}
                                        config={chartConfig}
                                        title={"VM Count of Memory Ballooning / CPU Ready"}
                                        height={473}
                                        navDisabled={true}
                                        rangeSelectorDisabled={true}
                                        disableLegend={false}
                            />
                        </VsiBubble>
                    </Col>
                </Row>
            </VsiBubble>
            <VsiBubble>
                <Grid addStyle
                      data={gridData}
                      isLoading={isLoading}
                      columnDefs={virtualGridDefs}
                      filterChanged={filterChanged}
                      rowDoubleClicked={onRowClick}
                      onSelectionChanged={onSelectionChanged}
                      searchDebounce={250}
                      customToolbarButton={user.role === "super_user" ? deleteDeviceBtn : <></>}
                      rowSelection={"single"}/>
            </VsiBubble>
        </Card>
    );
}
